import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Label } from '@nx/ui';
import { useI18n } from '@nx/i18n';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { authCleared, loginRequested } from '../store/slices/authSlice';

export function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isAuthenticating, authResult, identity } = useAppSelector((s) => s.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(authCleared());
  }, [dispatch]);

  useEffect(() => {
    if (identity) {
      navigate(identity.emailVerified ? '/dashboard' : '/verify-email', { replace: true });
    }
  }, [identity, navigate]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email || !password) {
      setLocalError('Please enter email and password.');
      return;
    }
    setLocalError(null);
    dispatch(loginRequested({ email, password }));
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-[#3D3328]/10 rounded-2xl shadow-lg p-6 md:p-8">
        <div className="mb-6 text-center">
          <h1
            className="text-2xl md:text-3xl text-[#3D3328] mb-2 font-bold"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {t('login.title')}
          </h1>
          <p className="text-sm text-[#78665A]">{t('login.subtitle')}</p>
        </div>

        {(localError || authResult.error) && (
          <Alert variant="destructive" title={t('login.failed')} className="mb-4">
            {localError ?? authResult.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('common.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isAuthenticating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('common.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              disabled={isAuthenticating}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isAuthenticating}>
            {isAuthenticating ? t('login.signingIn') : t('common.signIn')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-[#78665A]">
          {t('login.noAccount')}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="ml-2 text-[#1A1A1A] font-semibold hover:underline"
          >
            {t('common.signUp')}
          </button>
        </div>
      </div>
    </div>
  );
}
